/**
 * Pagina "Auto-trading" (SPEC-014 §7.1).
 *
 * ## Quattro cose che questa pagina deve dire, e che una tabella non direbbe
 *
 *  1. **Se l'interruttore globale è spento, niente qui ha effetto.** È la
 *     lezione di F-037, dove un toggle acceso non faceva nulla perché mancava
 *     il flag di server: l'avviso sta in cima, prima dei comandi, non accanto.
 *  2. **Un portafoglio senza destinazione non può eseguire.** `executable`
 *     arriva calcolato dal backend; qui la modalità «Esegui davvero» non compare
 *     affatto, con la ragione scritta sotto la select, invece di lasciar
 *     scoprire il problema dai rifiuti del giorno dopo.
 *  3. **`dry_run` non è «spento a metà».** Il motore fa tutto — incrocio,
 *     verifica, cancello, simulazione dei limiti — e registra l'esito che
 *     avrebbe avuto. È il modo in cui questa feature diventa collaudabile senza
 *     muovere denaro, e la pagina lo dichiara.
 *  4. **Ogni candidato scartato ha un motivo, e il motivo si legge.** Il codice
 *     resta visibile accanto alla traduzione: la frase serve a chi guarda, il
 *     codice a chi poi cerca nel registro.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { formatDate, formatDateTime, formatNumber } from '@/lib/format';
import { Alert, Badge, Button, Card, EmptyState, Field, Select, Spinner, Switch, TableWrap } from '@/ui';
import { AgenticBadge } from '@/features/agent-runs/AgenticBadge';
import {
  useAutoTradingRuns,
  useAutoTradingSettings,
  useRunAutoTradingNow,
  useUpdateAutoTradingSettings,
  type AutoTradingMode,
  type AutoTradingSettings,
  type Proposal,
  type Run,
} from './api';
import './auto-trading.css';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

/**
 * Il codice del salto viaggia nei `details` dell'errore, non nel messaggio: una
 * frase non si può confrontare né testare, ed è la stessa ragione per cui i
 * rifiuti delle proposte hanno un codice strutturato accanto alla prosa.
 */
function skipCodeOf(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const details = error.details as Array<{ path?: string; message?: string }> | undefined;
  return details?.find((detail) => detail.path === 'skipCode')?.message ?? null;
}

const MODE_LABEL: Record<AutoTradingMode, string> = {
  off: 'Spento',
  dry_run: 'Simulazione (dry run)',
  execute: 'Esegui davvero',
};

const STATUS_LABEL: Record<string, string> = {
  executed: 'Eseguita',
  dry_run: 'Simulata',
  rejected: 'Scartata',
  proposed: 'Proposta',
  // F-069: in attesa dell'apertura del mercato del titolo — un appuntamento,
  // non un esito.
  deferred: 'Rinviata',
};

/** Stessa tabella di traduzione della notifica: il codice resta comunque visibile. */
const REASON_LABEL: Record<string, string> = {
  wishlist_expired: 'riga di wishlist scaduta',
  wishlist_exceeded: 'vincolo di wishlist già saturo',
  no_price: 'nessun prezzo di riferimento',
  stale_data: 'prezzo troppo vecchio',
  no_broker_ticker: 'manca il ticker del broker',
  no_exchange_pair: "coppia non a catalogo sull'exchange",
  swap_unsupported: 'scambio non eseguibile su questo portafoglio',
  crypto_rank_out_of_range: 'fuori dalle prime 200 crypto per capitalizzazione',
  llm_reject: 'respinta nel merito dalla verifica',
  llm_check_failed: 'verifica non riuscita',
  llm_budget_exceeded: 'budget di verifica del run esaurito',
  broker_rejected: 'ordine rifiutato dal broker',
  limit_rejected: 'fermata da un tuo limite operativo, mai inviata al broker',
  paper_rejected: 'registrazione simulata non riuscita',
  cash_reserve: 'liquidità minima da preservare',
  market_closed: 'il mercato del titolo non ha una seduta oggi',
  no_fx_rate: 'cambio valuta non disponibile',
  portfolio_off: 'portafoglio disattivato nel frattempo',
  // F-171: una gemella (stesso portafoglio, titolo e verso) e' gia' stata eseguita oggi.
  superseded_by_execution: 'una proposta gemella è già stata eseguita oggi: non si ripete',
};

function describeReason(code: string | null): string {
  if (!code) return 'motivo non registrato';
  if (code.startsWith('limit_exceeded:')) {
    return `limite operativo raggiunto (${code.slice('limit_exceeded:'.length)})`;
  }
  return REASON_LABEL[code] ?? code;
}

/**
 * Perché un candidato NON è nato (F-137), e per la revisione dei profitti
 * perché una vendita chiesta dal modello non è arrivata al motore (F-138).
 * Stessi codici della scheda «profilo dei flussi» in Esiti.
 */
const DISCARD_LABEL: Record<string, string> = {
  hold_rating: 'giudizio «Hold»: nessuna direzione',
  no_instrument: 'titolo non a catalogo',
  new_position_without_wishlist: 'titolo nuovo senza wishlist',
  amount_not_computable: 'importo non calcolabile',
  below_min_amount: "sotto l'importo minimo",
  no_position_to_sell: 'nessuna posizione da vendere',
  quantity_not_positive: 'quantità non positiva',
  capped_by_max_proposals: 'oltre il tetto di proposte per run',
  position_unknown: 'titolo non in posizione',
  position_not_sellable: 'posizione non vendibile (in perdita o senza P&L noto)',
  sell_step_invalid: 'taglio non ammesso',
  duplicate_symbol: 'stesso titolo proposto due volte',
  pending_proposal_exists: 'una proposta gemella è già in attesa in un altro run',
};

// -----------------------------------------------------------------------------
// Configurazione per portafoglio
// -----------------------------------------------------------------------------

/**
 * Il fuso del browser, letto una volta.
 *
 * È l'unico posto che lo conosce davvero: il server non può indovinarlo, e
 * salvare l'ora in UTC la sposterebbe di un'ora per metà anno con l'ora legale.
 */
const reviewTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Rome';

function SettingsRow({ settings }: { settings: AutoTradingSettings }) {
  const update = useUpdateAutoTradingSettings();
  const [open, setOpen] = useState(false);
  const [maxProposals, setMaxProposals] = useState(String(settings.maxProposalsPerRun));
  const [minAmount, setMinAmount] = useState(settings.minOperationAmount ?? '');
  const [cashReserve, setCashReserve] = useState(settings.minCashReserve ?? '');
  const [cap, setCap] = useState(settings.concentrationCapPct ?? '');
  const [capMode, setCapMode] = useState(settings.concentrationEnforcement);
  const [freshness, setFreshness] = useState(String(settings.dataFreshnessMinutes));
  const [reviewEnabled, setReviewEnabled] = useState(settings.profitReviewEnabled);
  const [reviewTime, setReviewTime] = useState(settings.profitReviewTime ?? '17:30');
  const [allowSwaps, setAllowSwaps] = useState(settings.allowSwaps);
  // SPEC-026 §4.3: gli scambi hanno senso solo dove una moneta può stare.
  const canHostCrypto = settings.portfolioType === 'crypto' || settings.portfolioType === 'mixed' || settings.portfolioType === 'paper';

  const modeOptions = (['off', 'dry_run', 'execute'] as AutoTradingMode[])
    // «Esegui» sparisce se non c'è dove eseguire: mostrarlo disabilitato
    // richiederebbe di spiegare due volte la stessa cosa, e l'avviso sotto la
    // dice già una volta.
    .filter((mode) => mode !== 'execute' || settings.executable)
    .map((mode) => ({ value: mode, label: MODE_LABEL[mode] }));

  const saveDetails = () => {
    update.mutate(
      {
        portfolioId: settings.portfolioId,
        maxProposalsPerRun: Number(maxProposals),
        minOperationAmount: minAmount.trim() === '' ? null : minAmount.trim(),
        // Vuoto = nessun vincolo; uno zero scritto a mano è invece una soglia
        // vera («investi pure tutto, ma mai sotto zero») e va conservata.
        minCashReserve: cashReserve.trim() === '' ? null : cashReserve.trim(),
        // Vuoto = togli il tetto: la riga di limite viene cancellata, non spenta.
        concentrationCapPct: cap.trim() === '' ? null : cap.trim(),
        concentrationEnforcement: capMode,
        dataFreshnessMinutes: Number(freshness),
        profitReviewEnabled: reviewEnabled,
        profitReviewTime: reviewTime,
        allowSwaps,
        // ⚠️ Il fuso lo dichiara il BROWSER, e viaggia insieme all'ora. Senza,
        // il server dovrebbe indovinarlo — e un'ora salvata in UTC sarebbe
        // sbagliata di un'ora per metà anno per via dell'ora legale.
        profitReviewTimezone: reviewTimezone,
      },
      { onSuccess: () => setOpen(false) },
    );
  };

  return (
    <>
      <tr>
        <td>
          <span className="auto-trading__portfolio">{settings.portfolioName}</span>
          <span className="auto-trading__muted">
            {settings.portfolioType === 'paper' ? 'simulato' : settings.portfolioType} · {settings.baseCurrency}
          </span>
        </td>
        <td>
          <Select
            label="Modalità"
            value={settings.mode}
            options={modeOptions}
            onChange={(event) =>
              update.mutate({ portfolioId: settings.portfolioId, mode: event.target.value as AutoTradingMode })
            }
            disabled={update.isPending}
          />
          {!settings.executable && (
            <span className="auto-trading__muted">
              Nessuna destinazione: il portafoglio non è simulato e non ha una connessione broker attiva.
            </span>
          )}
        </td>
        <td className="cell--numeric">{settings.maxProposalsPerRun}</td>
        <td className="cell--numeric">
          {settings.minOperationAmount ? `${settings.minOperationAmount} ${settings.baseCurrency}` : '—'}
        </td>
        <td className="cell--numeric">
          {settings.minCashReserve ? `${settings.minCashReserve} ${settings.baseCurrency}` : '—'}
        </td>
        <td className="cell--numeric">{formatNumber(settings.dataFreshnessMinutes)} min</td>
        <td>
          <Button type="button" variant="ghost" onClick={() => setOpen((value) => !value)}>
            {open ? 'Chiudi' : 'Modifica'}
          </Button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7}>
            <div className="auto-trading__editor">
              <Field
                label="Proposte per esecuzione"
                value={maxProposals}
                onChange={(event) => setMaxProposals(event.target.value)}
                numeric
                hint="Da 1 a 10, per direzione. Tenerlo basso è il modo più semplice di limitare l'esposizione a un errore del modello."
              />
              <Field
                label={`Importo minimo (${settings.baseCurrency})`}
                value={minAmount}
                onChange={(event) => setMinAmount(event.target.value)}
                numeric
                hint="Sotto questa soglia il candidato non viene nemmeno proposto. Vuoto = nessuna soglia."
              />
              <Field
                label={`Liquidità minima non investita (${settings.baseCurrency})`}
                value={cashReserve}
                onChange={(event) => setCashReserve(event.target.value)}
                numeric
                hint="Vale solo per gli ACQUISTI: si compra solo se dopo l'operazione resta almeno questa liquidità. Se sei già sotto soglia gli acquisti si fermano, ma il motore non vende per rientrare — venderà solo su indicazione di vendita del titolo. Vuoto = nessun vincolo."
              />
              {/* Tetto di concentrazione (F-125). Sta accanto alla riserva di
                  liquidità perché è la difesa gemella: asimmetrica, sugli
                  acquisti, e non vende mai per rientrare. */}
              <Field
                label="Concentrazione massima per titolo (%)"
                value={cap}
                onChange={(event) => setCap(event.target.value)}
                numeric
                hint={
                  'Un singolo titolo non può pesare più di questa percentuale del patrimonio (titoli + liquidità). ' +
                  'Vale su (posseduto + operazione), quindi limita anche gli incrementi su una posizione già aperta. ' +
                  'Vuoto = nessun tetto.'
                }
              />
              {cap.trim() !== '' && (
                <Select
                  label="Cosa fa quando un acquisto lo supererebbe"
                  value={capMode}
                  onChange={(event) =>
                    setCapMode(event.target.value as 'observe' | 'reduce' | 'block')
                  }
                  options={[
                    { value: 'reduce', label: 'Riduci — ridimensiona al margine residuo' },
                    { value: 'block', label: 'Blocca — rifiuta l’operazione' },
                    { value: 'observe', label: 'Osserva — non rifiuta nulla, solo notifica' },
                  ]}
                  hint={
                    capMode === 'observe'
                      ? // ⚠️ Detto a parole, perché un limite che non limita è lo
                        // stato più pericoloso in cui lasciare una configurazione.
                        'Il tetto NON viene applicato: gli acquisti proseguono come se non ci fosse, e ricevi solo la notifica. Serve a guardare per qualche giorno cosa farebbe, prima di passare a «Riduci».'
                      : "Su un ordine MANUALE «Riduci» si comporta come «Blocca»: un ordine digitato da una persona non si esegue in una misura che nessuno ha scelto."
                  }
                />
              )}
              {settings.concentrationPreview.length > 0 && (
                <p className="auto-trading__muted">
                  Oggi sopra il tetto:{' '}
                  {settings.concentrationPreview
                    .map((row) => `${row.symbol} ${row.pct}%`)
                    .join(' · ')}
                  . Nessuno viene venduto: il tetto ferma gli acquisti, non liquida.
                </p>
              )}
              <Field
                label="Freschezza massima del prezzo (minuti)"
                value={freshness}
                onChange={(event) => setFreshness(event.target.value)}
                numeric
                hint="Oltre questo scarto il candidato è scartato con `stale_data`: un ordine su un prezzo vecchio è un ordine su un prezzo sbagliato."
              />

              {/* Scelta posizioni da vendere (F-123). Sta qui, fra le
                  impostazioni per portafoglio, perché è di questo portafoglio
                  che decide — e perché il tetto che la limita è quello
                  dichiarato due campi sopra. */}
              <Switch
                label="Scelta posizioni da vendere"
                checked={reviewEnabled}
                onChange={setReviewEnabled}
              />
              {/* `Switch` non ha un campo per la spiegazione: qui serve, perche'
                  e' l'unico interruttore di questa schermata che puo' far
                  partire una VENDITA da sola. */}
              <p className="auto-trading__muted">
                Una volta al giorno rilegge le posizioni aperte e chiede al modello se fra quelle{' '}
                <strong>in profitto</strong> ci sia qualcosa da vendere: nessuna, oppure fino a{' '}
                {maxProposals} vendite del 25%, 50% o 100% della posizione. Le vendite passano dal
                motore come tutte le altre — verifica, cancello, limiti operativi — quindi in
                «Simulazione» vengono registrate senza essere inviate. Non compra, e non vende in
                perdita.
              </p>
              {reviewEnabled && (
                <Field
                  label="Ora della revisione"
                  type="time"
                  value={reviewTime}
                  onChange={(event) => setReviewTime(event.target.value)}
                  hint={
                    `Ora locale (${reviewTimezone}). Il controllo gira a intervalli di pochi minuti, ` +
                    "quindi la revisione parte all'ora indicata o poco dopo — mai prima." +
                    (settings.profitReviewLastRunAt
                      ? ` Ultima revisione: ${formatDateTime(settings.profitReviewLastRunAt)}.`
                      : ' Non è ancora mai stata eseguita.')
                  }
                />
              )}
              {canHostCrypto && (
                <>
                  <Switch label="Scambi crypto→crypto" checked={allowSwaps} onChange={setAllowSwaps} />
                  <p className="auto-trading__muted">
                    Con questo consenso la revisione delle posizioni può proporre una <strong>rotazione</strong>:
                    vendere una criptovaluta e comprare con il ricavato un&apos;altra fra le prime 200 per
                    capitalizzazione (coppia diretta sull&apos;exchange, o due operazioni attraverso la valuta di
                    conto). Passa dal motore come una vendita con destinazione — verifica, cancello, limiti —
                    e in «Simulazione» viene solo registrata. Se la prima operazione non esegue, la seconda non
                    parte.
                  </p>
                </>
              )}
              {update.error && (
                <Alert variant="error" title="Modifica non salvata">
                  {errorMessage(update.error, 'Errore imprevisto')}
                </Alert>
              )}
              <div className="auto-trading__editor-actions">
                <Button type="button" onClick={saveDetails} disabled={update.isPending}>
                  {update.isPending ? 'Salvo…' : 'Salva'}
                </Button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Registro
// -----------------------------------------------------------------------------

function ProposalRow({ proposal }: { proposal: Proposal }) {
  const amount = proposal.executedAmount ?? proposal.approvedAmount ?? proposal.proposedAmount;
  return (
    <li className={`auto-trading__proposal auto-trading__proposal--${proposal.status}`}>
      <div className="auto-trading__proposal-head">
        <Badge tone={proposal.swapToSymbol ? 'accent' : proposal.side === 'buy' ? 'positive' : 'negative'}>
          {proposal.swapToSymbol ? 'Scambio' : proposal.side === 'buy' ? 'Acquisto' : 'Vendita'}
        </Badge>
        <strong>{proposal.swapToSymbol ? `${proposal.symbol} → ${proposal.swapToSymbol}` : proposal.symbol}</strong>
        <span className="auto-trading__muted">{proposal.portfolioName}</span>
        <Badge tone={proposal.status === 'executed' ? 'positive' : proposal.status === 'deferred' ? 'warning' : 'neutral'}>
          {STATUS_LABEL[proposal.status] ?? proposal.status}
        </Badge>
        {/* F-171: il nome leggibile. Quando uno scarto dice «esiste già
            Brave-Otter-Mango12», è qui che lo si ritrova a colpo d'occhio. */}
        {proposal.code && (
          <code className="auto-trading__code auto-trading__proposal-code" title="Codice della proposta">
            {proposal.code}
          </code>
        )}
        {/* Vale per QUALUNQUE stato, non solo per le eseguite: la domanda
            «perché questa non è andata a buon fine» è quella per cui si apre
            la vista tecnica (F-096). */}
        <Link to={`/auto-trading/proposals/${proposal.id}`} className="auto-trading__trace-link">
          riconcilia →
        </Link>
      </div>
      <div className="auto-trading__proposal-body">
        {amount && <span className="auto-trading__numeric">{amount}</span>}
        {proposal.quantity && <span className="auto-trading__muted">× {proposal.quantity}</span>}
        {proposal.status === 'rejected' && (
          <span>
            {describeReason(proposal.rejectionReasonCode)}
            {proposal.rejectionReasonCode && (
              <code className="auto-trading__code">{proposal.rejectionReasonCode}</code>
            )}
          </span>
        )}
        {proposal.status === 'deferred' && (
          <span>
            {/* L'appuntamento è un ISTANTE e va mostrato: «in attesa» senza
                un quando addestrerebbe a non fidarsi del rinvio. */}
            {proposal.deferredUntil
              ? `riprende alle ${formatDateTime(proposal.deferredUntil)}`
              : 'in attesa dell’apertura del mercato'}
          </span>
        )}
      </div>
      {proposal.llmReason && <p className="auto-trading__reason">{proposal.llmReason}</p>}
      {proposal.status === 'deferred' && proposal.deferReason && (
        <p className="auto-trading__reason">{proposal.deferReason}</p>
      )}
      {proposal.llmRiskFlags.length > 0 && (
        <div className="auto-trading__flags">
          {proposal.llmRiskFlags.map((flag) => (
            <Badge key={flag} tone="accent">
              {flag}
            </Badge>
          ))}
        </div>
      )}
    </li>
  );
}

function RunCard({ run }: { run: Run }) {
  const isReview = run.trigger === 'profit_review';
  return (
    <div className="auto-trading__run">
      <div className="auto-trading__run-head">
        <strong>{formatDate(run.runDate)}</strong>
        <Badge tone={run.status === 'failed' ? 'negative' : run.executedCount > 0 ? 'positive' : 'neutral'}>
          {run.status}
        </Badge>
        {/* Da cosa nasce il run (SPEC-019): un run del trigger su valore in
            mezzo agli schedulati senza etichetta sembrerebbe un doppione. */}
        {run.trigger === 'value_trigger' && <Badge tone="accent">trigger su valore</Badge>}
        {run.trigger === 'manual' && <Badge tone="neutral">manuale</Badge>}
        {/* F-144: le proposte le ha consegnate uno studio agentico (T10). Il
            dettaglio dice QUALE — «Nasdaq (XNAS) · revisione after-hours» —
            perche' il badge da solo direbbe solo come, non da dove. */}
        {run.trigger === 'agent' && <AgenticBadge />}
        {run.trigger === 'agent' && run.triggerSourceDetail && (
          <span className="auto-trading__muted">{run.triggerSourceDetail}</span>
        )}
        {/* SPEC-021: il nome è quello dell'interruttore, così la riga delle
            16:45 si riconosce dalla configurazione che l'ha prodotta. */}
        {isReview && <Badge tone="accent">scelta posizioni da vendere</Badge>}
        {isReview && run.triggerSourceDetail && (
          <span className="auto-trading__muted">{run.triggerSourceDetail}</span>
        )}
        <span className="auto-trading__muted">
          {run.candidatesCount} candidati · {run.executedCount} eseguiti · {run.rejectedCount} scartati
          {run.deferredCount > 0 && <> · {run.deferredCount} in attesa dell&apos;apertura</>}
        </span>
        <span className="auto-trading__muted">
          {formatNumber(run.llmTokens)} token · avviato {formatDateTime(run.startedAt)}
        </span>
      </div>

      {run.errorMessage && (
        <Alert variant="error" title={isReview ? 'Revisione non riuscita' : 'Esecuzione interrotta'}>
          {run.errorMessage}
        </Alert>
      )}

      {/* ⚠️ Il giudizio d'insieme NON è un errore: «nessuna vendita» è l'esito
          più frequente della revisione (SPEC-021), e va letto in nero, non in
          rosso. Prima della patch 0068 questo esito non lasciava alcuna riga. */}
      {run.summary && <p className="auto-trading__reason auto-trading__summary">{run.summary}</p>}

      {run.status === 'running' && (
        <p className="auto-trading__muted">
          {isReview ? 'Revisione avviata e non ancora conclusa.' : 'Esecuzione in corso.'}
        </p>
      )}

      {run.proposals.length === 0 ? (
        run.status !== 'running' &&
        !run.summary && (
          <p className="auto-trading__muted">Nessun candidato: il digest del giorno non ha prodotto nulla di azionabile.</p>
        )
      ) : (
        <ul className="auto-trading__proposals">
          {run.proposals.map((proposal) => (
            <ProposalRow key={proposal.id} proposal={proposal} />
          ))}
        </ul>
      )}

      {run.discards.length > 0 && (
        <details className="auto-trading__discards">
          <summary className="auto-trading__muted">
            {run.discards.length === 1
              ? '1 candidato non è nato'
              : `${run.discards.length} candidati non sono nati`}
            {isReview && ': vendite chieste dal modello e fermate prima del motore'}
          </summary>
          <ul className="auto-trading__discard-list">
            {run.discards.map((discard) => (
              <li key={discard.id}>
                <strong>{discard.symbol ?? '—'}</strong>{' '}
                <span>{DISCARD_LABEL[discard.reason] ?? discard.reason}</span>
                <code className="auto-trading__code">{discard.reason}</code>
                {discard.amount && <span className="auto-trading__muted"> · {discard.amount}</span>}
                {discard.detail && <p className="auto-trading__reason">{discard.detail}</p>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Pagina
// -----------------------------------------------------------------------------

export function AutoTradingPage() {
  const settings = useAutoTradingSettings();
  const runs = useAutoTradingRuns();
  const runNow = useRunAutoTradingNow();

  const items = settings.data?.items ?? [];
  const featureEnabled = settings.data?.featureEnabled ?? false;
  const executing = items.filter((row) => row.mode === 'execute');

  return (
    <div className="auto-trading">
      <header className="page-header">
        <div>
          <h1>Auto-trading</h1>
          <p className="page-header__subtitle">
            Dal briefing giornaliero all'ordine: il motore incrocia i verdetti con portafogli e wishlist,
            sottopone ogni proposta a una verifica e a un cancello deterministico, e — solo in modalità
            «Esegui davvero» — la invia rispettando i limiti operativi.
          </p>
        </div>
        <Button type="button" onClick={() => runNow.mutate(false)} disabled={runNow.isPending || !featureEnabled}>
          {runNow.isPending ? 'Eseguo…' : 'Esegui adesso'}
        </Button>
      </header>

      {!featureEnabled && (
        <Alert variant="warning" title="Motore spento sul server">
          <code>AUTO_TRADING_ENABLED</code> è a <code>false</code>: le impostazioni qui sotto si possono
          salvare, ma nessuna esecuzione parte finché la variabile non viene accesa nell'ambiente del
          backend. È il primo dei quattro livelli di difesa, ed è di proposito fuori dall'applicazione.
        </Alert>
      )}

      {featureEnabled && executing.length > 0 && (
        <Alert variant="warning" title={`${executing.length} ${executing.length === 1 ? 'portafoglio invia' : 'portafogli inviano'} ordini reali`}>
          {executing.map((row) => row.portfolioName).join(', ')}. Gli ordini restano soggetti ai limiti
          operativi e alle guardie della connessione broker, ma vengono inviati senza ulteriore conferma.
        </Alert>
      )}

      {runNow.error && (
        <Alert variant="error" title="Esecuzione non avviata">
          {errorMessage(runNow.error, 'Errore imprevisto')}
          {/* Solo `run_already_exists` è forzabile. Senza digest del giorno o
              senza portafogli attivi insistere non produrrebbe nulla, e offrire
              un pulsante che non può funzionare è il difetto che questa stessa
              pagina esiste per non ripetere. */}
          {skipCodeOf(runNow.error) === 'run_already_exists' && (
            <div className="auto-trading__force">
              <p>
                Se nel frattempo è cambiato qualcosa — per esempio hai aggiunto titoli — puoi far
                girare il motore una seconda volta. La nuova esecuzione resta registrata a parte,
                marcata come manuale, e <strong>rispetta gli stessi limiti operativi</strong>: quelli
                già consumati oggi non si azzerano.
              </p>
              <Button type="button" variant="danger" onClick={() => runNow.mutate(true)} disabled={runNow.isPending}>
                {runNow.isPending ? 'Eseguo…' : 'Forza una nuova esecuzione'}
              </Button>
            </div>
          )}
        </Alert>
      )}

      <Card
        title="Configurazione per portafoglio"
        subtitle="In «Simulazione» il motore fa tutto e registra l'esito che avrebbe avuto, senza inviare nulla."
        flush
      >
        {settings.isPending ? (
          <Spinner />
        ) : settings.error ? (
          <Alert variant="error" title="Impostazioni non caricate">
            {errorMessage(settings.error, 'Errore imprevisto')}
          </Alert>
        ) : items.length === 0 ? (
          <EmptyState
            icon="💼"
            title="Nessun portafoglio attivo"
            description="Il motore lavora sui portafogli attivi: creane uno per poterlo configurare."
          />
        ) : (
          <TableWrap>
            <table className="table">
              <thead>
                <tr>
                  <th>Portafoglio</th>
                  <th>Modalità</th>
                  <th className="cell--numeric">Proposte/run</th>
                  <th className="cell--numeric">Importo minimo</th>
                  <th className="cell--numeric">Liquidità minima</th>
                  <th className="cell--numeric">Freschezza</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <SettingsRow key={row.portfolioId} settings={row} />
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <Card
        title="Registro delle esecuzioni"
        subtitle="Ogni candidato lascia una riga, anche se scartato: è ciò che rende il motore ispezionabile."
      >
        {runs.isPending ? (
          <Spinner />
        ) : runs.error ? (
          <Alert variant="error" title="Registro non caricato">
            {errorMessage(runs.error, 'Errore imprevisto')}
          </Alert>
        ) : (runs.data?.items ?? []).length === 0 ? (
          <EmptyState
            icon="🤖"
            title="Nessuna esecuzione registrata"
            description="Il motore parte dalla chiusura del briefing giornaliero. Puoi anche avviarlo a mano con «Esegui adesso»."
          />
        ) : (
          <div className="auto-trading__runs">
            {(runs.data?.items ?? []).map((run) => (
              <RunCard key={run.id} run={run} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
